/**
 * Shared employer job lifecycle actions (PRD §7.6): publish a draft, take a
 * live role offline (with confirmation — applications are preserved), reopen
 * an offline role (prior distribution restored server-side), archive.
 *
 * 'delete' is the employer-facing wording for archive: the API refuses to
 * archive a live job (archive is never a silent unpublish), so a live role is
 * taken offline first and then archived behind ONE confirmation. Nothing is
 * destroyed — applicants and history survive, the role just leaves the page
 * and everyday management.
 *
 * One implementation serves the Careers sheet, the jobs list, and the job
 * detail screens so the transitions cannot drift per surface. Navigation to
 * the edit flow stays with the caller (the target route differs per surface).
 */
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { confirmDestructive } from '../lib/confirm-destructive';
import { getCompanyJobLifecycle } from '../lib/company-jobs';
import { useToast } from './useToast';
import { useHaptics } from './useHaptics';
import {
  useArchiveCompanyJob,
  useOfflineCompanyJob,
  usePublishCompanyJob,
  useReopenCompanyJob,
} from './useCompanyJobs';

export type JobLifecycleAction =
  | 'publish'
  | 'offline'
  | 'reopen'
  | 'archive'
  | 'delete';

/**
 * Context-appropriate transitions for a stored status (PRD §7.6 table).
 * 'delete' is deliberately absent: it is always available (it offlines first
 * when needed), so surfaces render it as its own affordance, not a menu item.
 */
export function availableJobLifecycleActions(
  status: string,
): JobLifecycleAction[] {
  const lifecycle = getCompanyJobLifecycle(status);
  if (lifecycle === 'draft') return ['publish', 'archive'];
  if (lifecycle === 'live') return ['offline'];
  if (lifecycle === 'offline') return ['reopen', 'archive'];
  return [];
}

export function useJobLifecycleActions() {
  const { t } = useTranslation('employer');
  const toast = useToast();
  const haptics = useHaptics();

  const publishMutation = usePublishCompanyJob();
  const offlineMutation = useOfflineCompanyJob();
  const reopenMutation = useReopenCompanyJob();
  const archiveMutation = useArchiveCompanyJob();

  const [pending, setPending] = useState<{
    jobId: string;
    action: JobLifecycleAction;
  } | null>(null);

  const run = useCallback(
    async (
      action: JobLifecycleAction,
      jobId: string,
      options: { status?: string } = {},
    ): Promise<boolean> => {
      if (pending) return false;

      // A live role cannot be archived directly, so the confirmation has to
      // say that deleting takes it offline on the way out.
      const wasLive = getCompanyJobLifecycle(options.status ?? '') === 'live';

      if (action === 'offline') {
        const confirmed = await confirmDestructive({
          title: t('jobFlow.lifecycle.offlineConfirmTitle') as string,
          message: t('jobFlow.lifecycle.offlineConfirmBody') as string,
          confirmLabel: t('jobFlow.lifecycle.offlineConfirmAction') as string,
          cancelLabel: t('jobFlow.lifecycle.cancel') as string,
        });
        if (!confirmed) return false;
      }

      if (action === 'archive') {
        const confirmed = await confirmDestructive({
          title: t('jobFlow.lifecycle.archiveConfirmTitle') as string,
          message: t('jobFlow.lifecycle.archiveConfirmBody') as string,
          confirmLabel: t('jobFlow.lifecycle.archiveConfirmAction') as string,
          cancelLabel: t('jobFlow.lifecycle.cancel') as string,
        });
        if (!confirmed) return false;
      }

      if (action === 'delete') {
        const confirmed = await confirmDestructive({
          title: t('jobFlow.lifecycle.deleteConfirmTitle') as string,
          message: t(
            wasLive
              ? 'jobFlow.lifecycle.deleteConfirmBodyLive'
              : 'jobFlow.lifecycle.deleteConfirmBody',
          ) as string,
          confirmLabel: t('jobFlow.lifecycle.deleteConfirmAction') as string,
          cancelLabel: t('jobFlow.lifecycle.cancel') as string,
        });
        if (!confirmed) return false;
      }

      setPending({ jobId, action });
      // Delete is two writes for a live role. Once the first one lands, a
      // failure of the second has already changed what candidates see, so the
      // error has to say that instead of the generic "could not update".
      let offlinedForDelete = false;
      try {
        if (action === 'publish') {
          await publishMutation.mutateAsync(jobId);
          toast.success(
            t('jobFlow.toasts.publishedTitle'),
            t('jobFlow.toasts.publishedBody'),
          );
        } else if (action === 'offline') {
          await offlineMutation.mutateAsync(jobId);
          toast.success(
            t('jobFlow.toasts.offlineTitle'),
            t('jobFlow.toasts.offlineBody'),
          );
        } else if (action === 'reopen') {
          await reopenMutation.mutateAsync({ jobId });
          toast.success(
            t('jobFlow.toasts.reopenedTitle'),
            t('jobFlow.toasts.reopenedBody'),
          );
        } else if (action === 'delete') {
          if (wasLive) {
            await offlineMutation.mutateAsync(jobId);
            offlinedForDelete = true;
          }
          await archiveMutation.mutateAsync(jobId);
          toast.success(
            t('jobFlow.toasts.deletedTitle'),
            t('jobFlow.toasts.deletedBody'),
          );
        } else {
          await archiveMutation.mutateAsync(jobId);
          toast.success(t('jobFlow.toasts.archivedTitle'));
        }
        haptics.trigger('success');
        return true;
      } catch (error) {
        if (offlinedForDelete) {
          // The role is still in the list but no longer on the careers page and
          // no longer accepting applications: a generic failure hid that
          // entirely. Not auto-reopened on purpose — the archive typically
          // failed because the API was unreachable or refused the transition,
          // so a third write is as likely to fail, and reopen re-runs
          // distribution server-side (re-announcing a role the employer was
          // trying to delete). So the employer decides: this explains the
          // half-done state and offers the retry.
          //
          // A dialog, not a toast: the toast HUD is a compact native overlay
          // that clips anything past a short line, and every word here (off
          // the page, applications stopped, Reopen is the way back) is the
          // part the employer has to act on. It is the same surface the
          // confirmations above already use for state-plus-next-action copy,
          // and the only one that works in this shared hook on web as well as
          // native (react-native's `Alert` has no web implementation).
          const retry = await confirmDestructive({
            title: t('jobFlow.lifecycle.deleteOfflinedOnlyTitle') as string,
            message: t('jobFlow.lifecycle.deleteOfflinedOnlyBody') as string,
            confirmLabel: t('jobFlow.lifecycle.deleteOfflinedOnlyRetry') as string,
            cancelLabel: t('jobFlow.lifecycle.deleteOfflinedOnlyDismiss') as string,
          });
          if (!retry) return false;
          try {
            await archiveMutation.mutateAsync(jobId);
            toast.success(
              t('jobFlow.toasts.deletedTitle'),
              t('jobFlow.toasts.deletedBody'),
            );
            haptics.trigger('success');
            return true;
          } catch (retryError) {
            // Still the same half-done state, and this is exactly when it
            // persists — so it stays on the dialog rather than falling back to
            // the toast, whose HUD would clip the one paragraph the employer
            // has to act on. The cause is appended, not substituted for it.
            //
            // Terminal by construction: offering "Try again" a second time is
            // what would loop, so the confirm button is the other route the
            // copy already names — Reopen, which puts the role back live.
            const cause =
              retryError instanceof Error ? retryError.message : undefined;
            const body = t(
              'jobFlow.lifecycle.deleteOfflinedOnlyRetryFailedBody',
            ) as string;
            const reopen = await confirmDestructive({
              title: t('jobFlow.lifecycle.deleteOfflinedOnlyTitle') as string,
              message: cause ? `${body}\n\n${cause}` : body,
              confirmLabel: t(
                'jobFlow.lifecycle.deleteOfflinedOnlyReopen',
              ) as string,
              cancelLabel: t(
                'jobFlow.lifecycle.deleteOfflinedOnlyDismiss',
              ) as string,
            });
            if (!reopen) return false;
            try {
              await reopenMutation.mutateAsync({ jobId });
              toast.success(
                t('jobFlow.toasts.reopenedTitle'),
                t('jobFlow.toasts.reopenedBody'),
              );
              haptics.trigger('success');
            } catch (reopenError) {
              toast.error(
                t('jobFlow.lifecycle.failedTitle'),
                reopenError instanceof Error ? reopenError.message : undefined,
              );
            }
            // Either way the delete did not happen.
            return false;
          }
        }
        toast.error(
          t('jobFlow.lifecycle.failedTitle'),
          error instanceof Error ? error.message : undefined,
        );
        return false;
      } finally {
        setPending(null);
      }
    },
    [
      archiveMutation,
      haptics,
      offlineMutation,
      pending,
      publishMutation,
      reopenMutation,
      t,
      toast,
    ],
  );

  const isPending = useCallback(
    (jobId: string, action?: JobLifecycleAction) =>
      pending !== null &&
      pending.jobId === jobId &&
      (action === undefined || pending.action === action),
    [pending],
  );

  return { run, pending, isPending };
}
